import { Core, Form, Localization, Responsive } from 'connex-cds';
import { filter, map, find } from 'lodash';
import React, { useMemo } from 'react';
import { useCompanySetup, useVehicleTypeSetup, useVehicleSetup } from '../../../../../MasterDataProvider';
import { useTicketContext } from '../../../../../TicketContext';
import { Desktop } from './Desktop';
import { Mobile } from './Mobile';

const { Translate, useTranslateMessage } = Localization;

export const StatusTimeline = ({ data }) => {
  const { ticket } = useTicketContext();
  const { values } = Form.useFormContext();
  const companySetup = useCompanySetup();
  const translateMessage = useTranslateMessage();
  const vehicleTypeSetup = useVehicleTypeSetup();
  const vehicleSetup = useVehicleSetup();

  const primaryLineItem = useMemo(() => find(ticket?.lineItems, { isPrimary: true }), [ticket?.lineItems]);

  const vehicleTypeData = useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: ticket?.vehicleId });
    return find(vehicleTypeSetup.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
  }, [vehicleSetup.data, vehicleTypeSetup.data, ticket?.vehicleId]);

  const mergedTicketEvents = { ...ticket?.ticketEvents, ...values?.ticketEvents };

  const ticketEvents = map(mergedTicketEvents, (value, key) => ({ eventType: key, ...value }));

  const filteredEvents = filter(ticketEvents, event => {
    if (ticket?.customData?.ticketType === 'PMP') {
      switch (event?.eventType) {
        case 'PRINTED':
          if (vehicleTypeData?.deliveriesConfiguration?.pump?.ticketed?.visible === false) return false;
          else return true;
        case 'TO_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.pump?.toJob?.visible === false) return false;
          else return true;
        case 'ARRIVE_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.pump?.arriveJob?.visible === false) return false;
          else return true;
        case 'PUMPING_STARTED':
          if (vehicleTypeData?.deliveriesConfiguration?.pump?.startPump?.visible === false) return false;
          else return true;
        case 'PUMPING_COMPLETE':
          if (vehicleTypeData?.deliveriesConfiguration?.pump?.finishPump?.visible === false) return false;
          else return true;
        case 'READY_TO_PUMP':
          if (vehicleTypeData?.deliveriesConfiguration?.pump?.readyToPump?.visible === false) return false;
          else return true;
        case 'IN_YARD':
          if (vehicleTypeData?.deliveriesConfiguration?.pump?.arrivePlant?.visible === false) return false;
          else return true;
        case 'LEAVE_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.pump?.departJob?.visible === false) return false;
          else return true;
        default:
          return false;
      }
    } else if (primaryLineItem?.productType?.systemProductType === 'AGGREGATE') {
      switch (event?.eventType) {
        case 'PRINTED':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.ticketed?.visible === false) return false;
          else return true;
        case 'LOADING_STARTED':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.loading?.visible === false) return false;
          else return true;
        case 'LOADING_COMPLETE':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.loaded?.visible === false) return false;
          else return true;
        case 'TO_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.toJob?.visible === false) return false;
          else return true;
        case 'ARRIVE_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.arriveJob?.visible === false) return false;
          else return true;
        case 'UNLOADING':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.beginUnload?.visible === false) return false;
          else return true;
        case 'END_UNLOADING':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.endUnload?.visible === false) return false;
          else return true;
        case 'LEAVE_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.departJob?.visible === false) return false;
          else return true;
        case 'IN_YARD':
          if (vehicleTypeData?.deliveriesConfiguration?.aggregate?.arrivePlant?.visible === false) return false;
          else return true;
        default:
          return false;
      }
    } else {
      switch (event?.eventType) {
        case 'PRINTED':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.ticketed?.visible === false) return false;
          else return true;
        case 'LOADING_STARTED':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.loading?.visible === false) return false;
          else return true;
        case 'LOADING_COMPLETE':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.loaded?.visible === false) return false;
          else return true;
        case 'TO_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.toJob?.visible === false) return false;
          else return true;
        case 'ARRIVE_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.arriveJob?.visible === false) return false;
          else return true;
        case 'UNLOADING':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.beginUnload?.visible === false) return false;
          else return true;
        case 'END_UNLOADING':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.endUnload?.visible === false) return false;
          else return true;
        case 'LEAVE_JOB':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.departJob?.visible === false) return false;
          else return true;
        case 'IN_YARD':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.arrivePlant?.visible === false) return false;
          else return true;
        case 'WASHING':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.beginWash?.visible === false) return false;
          else return true;
        case 'END_WASHING':
          if (vehicleTypeData?.deliveriesConfiguration?.readyMix?.endWash?.visible === false) return false;
          else return true;
        default:
          return false;
      }
    }
  });

  if (
    ticket?.customData?.productDetails?.wcContactDateTime &&
    !(vehicleTypeData?.deliveriesConfiguration?.readyMix?.firstWaterCementContact?.visible === false)
  ) {
    const wCContact = {
      eventType: 'FIRST_WCCONTACT',
      eventDateTime: ticket?.customData?.productDetails?.wcContactDateTime,
    };
    filteredEvents.push(wCContact);
  }

  // We need to map our object to the requirements of the Timeline component.
  // Optionally, action can be added to zero or more items and, if present, will be rendered
  // to the right of the description.
  const items = React.useMemo(() => {
    return filteredEvents.map(event => ({
      isoDateString: event.eventDateTime,
      event: translateMessage(event.eventType),
      description: (
        <div>
          <div style={{ display: 'flex' }}>
            <div>
              <Translate stringId={event.eventType} />
            </div>
          </div>
        </div>
      ),
    }));
  }, [filteredEvents, translateMessage]);

  return (
    <Responsive>
      <Mobile items={items} />
      <Desktop items={items} />
    </Responsive>
  );
};
